/**
 * AP Port Profiles (Wireless > AP Port Profiles; was "Port Profiles" until OP#1858) — reusable wall-plate AP port config.
 *
 * Meridian's take on Meraki/UniFi AP port profiles, in the app's own design language.
 * Two tabs:
 *   Configure — manage named wired networks (label + VLAN) and port profiles (a per-port map:
 *               each AP ethernet port -> Uplink trunk, or Access bridged to a wired network's
 *               tagged VLAN).
 *   Assign APs — pick a profile, select wall-plate APs, apply (renders + owgw-pushes per AP).
 *               OP#1937: Apply asks first (lists the APs, states the ~4 min outage PER AP),
 *               pushes one AP at a time and shows a result per AP (lib/apPortApply). Saving a
 *               profile never pushes; a banner says how many APs still run the old config.
 *
 * Site-scoped: wired VLANs + their port mapping are a per-site network design. Requires a site
 * in scope (the top ScopeBar); "All sites" shows a prompt to pick one.
 */
import { useMemo, useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { useSession } from "@/stores/session";
import {
  useWiredNetworks,
  useCreateWiredNetwork,
  useDeleteWiredNetwork,
  usePortProfiles,
  useCreatePortProfile,
  useUpdatePortProfile,
  useDeletePortProfile,
  useAssignPortProfile,
  useMakeDefaultPortProfile,
  useDevices,
} from "@/lib/api/hooks";
import { PageHeader, ErrorBanner, EmptyState } from "@/components/ui/Page";
import { Button } from "@/components/ui/Button";
import { RoleGate } from "@/components/auth/RoleGate";
import { useSiteVlans, type SiteVlanRow } from "@/lib/api/siteVlans";
// Reuse the ONE UI VLAN-list grammar (comma + A-B ranges, dedupe, 1-4094) the switch port
// table already ships; the backend re-parses and is authoritative. Pre-validation only.
import { parseVlanList } from "@/lib/portTable";
// Pure, unit-tested builder-row logic (canonical ports, labels, serialization, allow-list).
import {
  type DialogRow, buildProfileRows, canonicalPortLabel, hasInvalidAllowed, rowsToPorts,
} from "@/lib/apPortProfile";
import type { WiredNetwork, ApPortProfile } from "@/types/domain";
import {
  type ApApplyResult, applyConfirmText, applySummary, applyToAps, profileDriftBanner,
} from "@/lib/apPortApply";

const selCls = "rounded border border-line bg-surface px-2 py-1 text-sm text-ink outline-none focus:border-brand";

export default function ApPortProfiles() {
  const siteId = useSession((s) => s.currentSiteId);
  const [tab, setTab] = useState<"configure" | "assign">("configure");

  if (!siteId) {
    return (
      <div>
        <PageHeader title="AP Port Profiles" subtitle="Reusable wall-plate AP port configuration" />
        <EmptyState title="Pick a site" hint="Port profiles are per-site. Choose a site in the scope bar above." />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="AP Port Profiles" subtitle="Reusable wall-plate AP port configuration" />
      {/* Tabs */}
      <div className="mb-4 flex gap-1 border-b border-line">
        {(["configure", "assign"] as const).map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => setTab(t)}
            className={
              "min-h-touch px-3 text-sm font-medium -mb-px border-b-2 " +
              (tab === t ? "border-brand text-ink" : "border-transparent text-ink-muted hover:text-ink")
            }
          >
            {t === "configure" ? "Configure" : "Assign APs"}
          </button>
        ))}
      </div>
      {tab === "configure" ? <ConfigureTab siteId={siteId} /> : <AssignTab siteId={siteId} />}
    </div>
  );
}

/* ------------------------------- Configure tab ------------------------------- */
function ConfigureTab({ siteId }: { siteId: string }) {
  const nets = useWiredNetworks(siteId);
  const profiles = usePortProfiles(siteId);
  const vlans = useSiteVlans(siteId);
  const networks = nets.data?.items ?? [];
  const siteVlans = vlans.data?.items ?? [];
  // OP#1937: after saving a profile that APs use, say plainly that those APs still run the OLD
  // port config until it is applied. Set from the PUT response's used_by count.
  const [savedNotice, setSavedNotice] = useState<string | null>(null);
  const onSaved = (p: ApPortProfile) => setSavedNotice(profileDriftBanner(p.name, p.used_by));

  return (
    <div className="flex flex-col gap-6">
      {savedNotice && (
        <div role="status" data-testid="profile-drift-banner"
          className="flex items-start justify-between gap-3 rounded border border-warn bg-warn-soft p-3 text-sm text-ink">
          <span>{savedNotice}</span>
          <button type="button" className="text-xs text-ink-muted hover:text-ink" onClick={() => setSavedNotice(null)}>
            Dismiss
          </button>
        </div>
      )}
      {/* Wired networks */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Wired networks</h2>
          <RoleGate min="site_operator" mode="hide"><AddNetwork siteId={siteId} /></RoleGate>
        </div>
        {nets.error && <ErrorBanner error={nets.error} />}
        {networks.length === 0 ? (
          <p className="text-sm text-ink-subtle">No wired networks yet. Add one (a name + VLAN) to map ports to it.</p>
        ) : (
          <ul className="flex flex-wrap gap-2">
            {networks.map((n) => (
              <li key={n.id} className="flex items-center gap-2 rounded-md border border-line bg-surface px-2.5 py-1.5">
                <span className="text-sm font-medium text-ink">{n.name}</span>
                <span className="rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">VLAN {n.vlan_id}</span>
                <RoleGate min="site_operator" mode="hide">
                  <DeleteNetwork siteId={siteId} id={n.id} />
                </RoleGate>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Profiles */}
      <section>
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-muted">Profiles</h2>
          <RoleGate min="site_operator" mode="hide">
            <ProfileDialog siteId={siteId} networks={networks} siteVlans={siteVlans} trigger={<Button variant="primary">New profile</Button>} />
          </RoleGate>
        </div>
        {profiles.error && <ErrorBanner error={profiles.error} />}
        {/* OP#1937: the old line here claimed an edit "reaches every AP it is applied to" -- it
            never did (PUT saves the row, nothing is pushed). Saving changes no AP; applying
            (Assign APs) pushes, ~4 min outage per AP. Per-AP port overrides still win. */}
        <p className="mb-2 text-xs text-ink-subtle">
          Saving a profile does not change any AP. APs using it keep their current port setup
          until you apply the profile from Assign APs (each AP is offline for about 4 minutes
          while it applies). Ports overridden on an individual AP keep their override.
        </p>
        {(profiles.data?.items?.length ?? 0) === 0 ? (
          <EmptyState title="No port profiles yet" hint="Create a profile mapping each AP port to a wired network or the uplink." />
        ) : (
          <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {profiles.data!.items.map((p) => (
              <ProfileCard key={p.id} siteId={siteId} profile={p} networks={networks} siteVlans={siteVlans} onSaved={onSaved} />
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function ProfileCard({ siteId, profile, networks, siteVlans, onSaved }: {
  siteId: string; profile: ApPortProfile; networks: WiredNetwork[]; siteVlans: SiteVlanRow[];
  onSaved: (p: ApPortProfile) => void;
}) {
  const del = useDeletePortProfile(siteId);
  const makeDefault = useMakeDefaultPortProfile(siteId);
  const netName = (id?: number | null) => networks.find((n) => n.id === id)?.name ?? `network ${id}`;
  // Site VLAN label for a direct VLAN binding, e.g. "1254 Media Streaming".
  const vlanLabel = (id: number) => {
    const v = siteVlans.find((s) => s.vlan_id === id);
    return v ? `${v.vlan_id} ${v.vlan_name}` : `VLAN ${id}`;
  };
  const ports = Object.entries(profile.ports ?? {});
  return (
    <li className="md-card flex flex-col gap-2 p-4">
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="font-medium text-ink">{profile.name}</span>
          {/* CURRENT DEFAULT is a neutral outline chip, not a status colour: being the default
              is an editorial fact about the site, not an operational state of anything. */}
          {profile.is_default && (
            <span className="rounded border border-line px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-ink-muted">
              Current default
            </span>
          )}
          {/* How many APs carry it (OP#1937) -- the blast radius of an Apply. */}
          {profile.used_by != null && (
            <span className="text-[11px] text-ink-subtle" data-testid="profile-used-by">
              Used by {profile.used_by} AP{profile.used_by === 1 ? "" : "s"}
            </span>
          )}
        </div>
        <RoleGate min="site_operator" mode="hide">
          <div className="flex items-center gap-2">
            {!profile.is_default && (
              <button type="button" disabled={makeDefault.isPending}
                onClick={() => makeDefault.mutate(profile.id)}
                className="text-xs font-medium text-brand hover:underline">Make default</button>
            )}
            <ProfileDialog siteId={siteId} networks={networks} siteVlans={siteVlans} existing={profile}
              onSaved={onSaved}
              trigger={<button type="button" className="text-xs font-medium text-brand hover:underline">Edit</button>} />
            <button type="button" disabled={del.isPending} onClick={() => del.mutate(profile.id)}
              className="text-xs text-ink-muted hover:text-critical">Delete</button>
          </div>
        </RoleGate>
      </div>
      <dl className="flex flex-col gap-1 text-sm">
        {ports.length === 0 && <span className="text-ink-subtle">No ports mapped.</span>}
        {ports.map(([port, spec]) => (
          <div key={port} className="flex items-center justify-between gap-2">
            <dt className="flex min-w-0 items-baseline gap-1.5">
              <span className="font-mono text-xs text-ink-muted">{port.replace(/\*$/, "")}</span>
              {/* The friendly wall-plate name the operator gave this port ("TV"). */}
              {spec.role !== "uplink" && spec.label && (
                <span className="truncate text-xs font-medium text-ink">{spec.label}</span>
              )}
            </dt>
            <dd className="flex flex-wrap items-center justify-end gap-1 text-ink">
              {spec.role === "uplink" ? (
                <>
                  <span className="text-ink-muted">Uplink (trunk)</span>
                  {/* The trunk allow-list, if the profile restricts it; absent = all VLANs. */}
                  {spec.allowed_vlans && spec.allowed_vlans.length > 0 && (
                    <span className="rounded border border-line px-1.5 py-0.5 text-[10px] text-ink-subtle"
                      title={`Allowed VLANs: ${spec.allowed_vlans.join(", ")}`}>
                      {spec.allowed_vlans.length} VLAN{spec.allowed_vlans.length === 1 ? "" : "s"}
                    </span>
                  )}
                </>
              ) : spec.vlan != null ? (
                <span className="rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">{vlanLabel(spec.vlan)}</span>
              ) : spec.network_id != null ? (
                <span className="rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">{netName(spec.network_id)}</span>
              ) : (
                <span className="text-ink-subtle">Untagged</span>
              )}
            </dd>
          </div>
        ))}
      </dl>
    </li>
  );
}

/* ------------------------------- Assign APs tab ------------------------------- */
function AssignTab({ siteId }: { siteId: string }) {
  const profiles = usePortProfiles(siteId);
  const { data } = useDevices();
  const assign = useAssignPortProfile();
  // The site's default profile is the pre-selection -- the ONLY thing "default" does. It
  // pushes nothing on its own; assigning is still the explicit, audited step.
  const defaultId = (profiles.data?.items ?? []).find((p) => p.is_default)?.id;
  const [profileId, setProfileId] = useState("");
  const chosen = profileId || (defaultId != null ? String(defaultId) : "");
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // OP#1937: Apply asks first; results are kept PER AP so no failure hides behind a success.
  const [asking, setAsking] = useState(false);
  const [running, setRunning] = useState(false);
  const [results, setResults] = useState<ApApplyResult[] | null>(null);

  // Wall-plate APs at THIS site (the profile targets the AP's ethernet ports).
  const aps = useMemo(
    () => (data?.items ?? []).filter((d) => d.kind === "access_point" && String(d.site_id) === String(siteId)),
    [data, siteId],
  );
  const toggle = (s: string) =>
    setSelected((prev) => { const n = new Set(prev); n.has(s) ? n.delete(s) : n.add(s); return n; });

  const chosenName = (profiles.data?.items ?? []).find((p) => String(p.id) === chosen)?.name ?? "";
  const targets = aps.filter((d) => selected.has(d.serial));
  const confirmText = applyConfirmText(chosenName, targets.length);

  // Runs only from the confirm panel. One AP at a time (mutateAsync awaited in turn), every
  // AP's outcome recorded -- the old forEach(mutate) kept only the last AP's error.
  const apply = async () => {
    if (!chosen || targets.length === 0) return;
    const pid = Number(chosen);
    setAsking(false);
    setRunning(true);
    setResults([]);
    try {
      await applyToAps(
        targets.map((d) => ({ serial: d.serial, name: d.name })),
        (serial) => assign.mutateAsync({ serial, profile_id: pid }),
        (r) => setResults((prev) => [...(prev ?? []), r]),
      );
    } finally {
      setRunning(false);
    }
  };
  const summary = results && !running ? applySummary(results) : null;

  return (
    <RoleGate min="site_operator" mode="hide" fallback={<p className="text-sm text-ink-muted">You have read-only access.</p>}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <select className={selCls} value={chosen} onChange={(e) => { setProfileId(e.target.value); setResults(null); setAsking(false); }}>
            <option value="">Choose a profile…</option>
            {(profiles.data?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>{p.name}{p.is_default ? " (default)" : ""}</option>
            ))}
          </select>
          <Button variant="primary" disabled={!chosen || targets.length === 0 || running || asking}
            onClick={() => { setResults(null); setAsking(true); }}>
            Apply to {targets.length} AP{targets.length === 1 ? "" : "s"}
          </Button>
          {running && <span className="text-xs text-ink-muted">Applying… {results?.length ?? 0} of {targets.length} done</span>}
          {summary && (
            <span data-testid="apply-summary"
              className={`text-xs ${summary.tone === "ok" ? "text-healthy" : "text-critical"}`}>
              {summary.text}
            </span>
          )}
        </div>

        {/* The confirm (in-page alertdialog, never window.confirm): lists every AP and states
            the ~4 minute outage per AP. Nothing is pushed until "Apply and push" is clicked. */}
        {asking && (
          <div role="alertdialog" aria-labelledby="apply-confirm-title" aria-describedby="apply-confirm-impact"
            data-testid="apply-confirm" className="rounded border border-warn bg-warn-soft p-3 text-sm">
            <p id="apply-confirm-title" className="font-semibold text-ink">{confirmText.title}</p>
            <p id="apply-confirm-impact" className="mt-1 text-ink">{confirmText.impact}</p>
            <ul className="mt-2 max-h-48 list-disc overflow-y-auto pl-5 text-ink">
              {targets.map((d) => (
                <li key={d.serial}>
                  {d.name} <span className="font-mono text-xs text-ink-subtle">{d.serial}</span>
                </li>
              ))}
            </ul>
            <div className="mt-3 flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setAsking(false)}>Cancel</Button>
              <Button variant="danger" onClick={() => void apply()}>
                Apply and push to {targets.length} AP{targets.length === 1 ? "" : "s"}
              </Button>
            </div>
          </div>
        )}

        {/* Per-AP results: every AP gets its own line, failures in red with the backend reason
            (e.g. the port-compatibility rejection), so "applied" can never mask a failure. */}
        {results && results.length > 0 && (
          <ul className="flex flex-col gap-1" data-testid="apply-results">
            {results.map((r) => (
              <li key={r.serial} data-testid={`apply-result-${r.serial}`}
                className={`text-xs ${r.ok ? "text-healthy" : "text-critical"}`}>
                {r.name} ({r.serial}):{" "}
                {r.ok ? (r.unchanged ? "already up to date, nothing pushed." : "applied.") : `failed: ${r.error}`}
              </li>
            ))}
          </ul>
        )}
        {aps.length === 0 ? (
          <EmptyState title="No access points at this site" hint="Assign wall-plate APs to this site first." />
        ) : (
          <ul className="flex flex-col gap-1">
            {aps.map((d) => (
              <li key={d.serial} className="flex items-center gap-3 rounded-md border border-line bg-surface px-3 py-2">
                <input type="checkbox" className="h-4 w-4 accent-brand" checked={selected.has(d.serial)}
                  onChange={() => toggle(d.serial)} aria-label={`Select ${d.serial}`} />
                <span className="min-w-0 flex-1 truncate text-sm text-ink">{d.name}</span>
                {d.port_profile_name && (
                  <span className="rounded bg-brand/10 px-1.5 py-0.5 text-xs text-brand">{d.port_profile_name}</span>
                )}
                <span className="truncate text-xs text-ink-muted">{d.model}</span>
                <span className="font-mono text-xs text-ink-subtle">{d.serial}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </RoleGate>
  );
}

/* ------------------------------- Dialogs ------------------------------- */
function AddNetwork({ siteId }: { siteId: string }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [vlan, setVlan] = useState("");
  const create = useCreateWiredNetwork(siteId);
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const v = Number(vlan);
    if (!name.trim() || !(v >= 1 && v <= 4094)) return;
    create.mutate({ name: name.trim(), vlan_id: v }, { onSuccess: () => { setOpen(false); setName(""); setVlan(""); } });
  };
  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild><Button variant="secondary">Add network</Button></Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet w-full max-w-sm -translate-x-1/2 -translate-y-1/2 rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">Add wired network</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">A named VLAN a port can bridge to (e.g. "IP_Phones" = VLAN 30).</Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm"><span className="text-ink-muted">Name</span>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="IP_Phones" className={selCls} /></label>
            <label className="flex flex-col gap-1 text-sm"><span className="text-ink-muted">VLAN (1–4094)</span>
              <input value={vlan} inputMode="numeric" onChange={(e) => setVlan(e.target.value.replace(/[^0-9]/g, ""))} placeholder="30" className={selCls} /></label>
            {create.isError && <span className="text-xs text-critical">Could not create (duplicate name?).</span>}
            <div className="md-dialog-footer flex justify-end gap-2">
              <Dialog.Close asChild><Button variant="ghost" type="button">Cancel</Button></Dialog.Close>
              <Button variant="primary" type="submit" disabled={create.isPending}>Create</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function DeleteNetwork({ siteId, id }: { siteId: string; id: number }) {
  const del = useDeleteWiredNetwork(siteId);
  return (
    <button type="button" aria-label="Delete network" disabled={del.isPending}
      onClick={() => del.mutate(id)} className="text-xs text-ink-muted hover:text-critical">✕</button>
  );
}

/** Create/edit a profile. The port rows are the AP's CANONICAL ports -- WAN* (the uplink trunk)
 *  and LAN1..LAN4 (wall-plate access) -- and the operator CANNOT retype the port identifier
 *  (that was the 2026-09-15 bug: free-text friendly names became port keys that matched nothing).
 *  Each LAN row instead has a read-only port label, an editable Name (the friendly label, stored
 *  as `label`), and a VLAN / wired-network binding; the WAN row keeps its Allowed-VLANs list.
 *  Row logic lives in lib/apPortProfile so it is unit-testable. */
function ProfileDialog({ siteId, networks, siteVlans, existing, trigger, onSaved }: {
  siteId: string; networks: WiredNetwork[]; siteVlans: SiteVlanRow[];
  existing?: ApPortProfile; trigger: React.ReactNode;
  /** Called with the PUT response (carries used_by) after an edit is saved -- OP#1937. */
  onSaved?: (p: ApPortProfile) => void;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(existing?.name ?? "");
  // The fixed canonical set (WAN* + LAN1..LAN4), with any existing bindings/labels folded in,
  // plus a legacy row for any older non-canonical key so editing never drops a mapping.
  const [rows, setRows] = useState<DialogRow[]>(() => buildProfileRows(existing));
  const create = useCreatePortProfile(siteId);
  const update = useUpdatePortProfile(siteId);
  const busy = create.isPending || update.isPending;

  const setRow = (i: number, patch: Partial<DialogRow>) =>
    setRows((r) => r.map((row, idx) => (idx === i ? { ...row, ...patch } : row)));
  const removeRow = (i: number) => setRows((r) => r.filter((_, idx) => idx !== i));

  // Any uplink row whose Allowed-VLANs text has a malformed token blocks Save -- the same
  // grammar the backend enforces, surfaced here so the operator sees it before the round-trip.
  const badAllowed = hasInvalidAllowed(rows);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim() || badAllowed) return;
    const ports = rowsToPorts(rows);
    const onSuccess = () => setOpen(false);
    // Saving NEVER pushes to APs (OP#1937); the caller shows the "N APs use this profile" banner.
    if (existing) {
      update.mutate({ id: existing.id, name: name.trim(), ports }, {
        onSuccess: (saved) => { setOpen(false); onSaved?.(saved); },
      });
    } else create.mutate({ name: name.trim(), ports }, { onSuccess });
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Trigger asChild>{trigger}</Dialog.Trigger>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-sheet bg-ink/40" />
        <Dialog.Content className="md-dialog fixed left-1/2 top-1/2 z-sheet max-h-[85vh] w-full max-w-lg -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg border border-line bg-overlay p-5 shadow-sheet outline-none">
          <Dialog.Title className="text-lg font-semibold text-ink">{existing ? "Edit" : "New"} port profile</Dialog.Title>
          <Dialog.Description className="mt-1 text-sm text-ink-muted">
            Name each wall-plate LAN port and bind it to a VLAN. WAN is the uplink trunk.
          </Dialog.Description>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3">
            <label className="flex flex-col gap-1 text-sm"><span className="text-ink-muted">Profile name</span>
              <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Guest Rooms" className={selCls} /></label>
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium uppercase tracking-wide text-ink-muted">Ports</span>
              {rows.map((row, i) => (
                <div key={row.port || i} className="rounded-md border border-line bg-surface-2 p-2">
                  <div className="flex flex-wrap items-center gap-2">
                    {/* The port identifier is READ-ONLY -- these are the AP's real ports, not a
                        free-text field. A monospace chip carries the canonical name. */}
                    <span className="inline-flex min-w-[3.5rem] items-center justify-center rounded border border-line bg-surface px-2 py-1 font-mono text-xs font-medium text-ink-muted"
                      title={row.port}>
                      {canonicalPortLabel(row.port)}
                    </span>

                    {row.role === "uplink" ? (
                      // The uplink trunk -- fixed role, not an access port.
                      <span className="text-sm text-ink-muted">Uplink (trunk)</span>
                    ) : (
                      <>
                        {/* The friendly wall-plate NAME (stored as `label`). This is the field the
                            owner types -- "TV", "AppleTV" -- and it is NOT the port key. */}
                        <input value={row.name} onChange={(e) => setRow(i, { name: e.target.value })}
                          placeholder="Name (e.g. TV)" className={`${selCls} w-32`} aria-label="Port name" />
                        {/* ACCESS target: a site-VLAN picker, or (via the affordance below) a wired network. */}
                        {row.target === "vlan" ? (
                          <select className={`${selCls} flex-1`} value={row.vlan ?? ""} aria-label="Access VLAN"
                            onChange={(e) => setRow(i, { vlan: e.target.value ? Number(e.target.value) : null })}>
                            <option value="">VLAN…</option>
                            {siteVlans.map((v) => (
                              <option key={v.id} value={v.vlan_id}>{v.vlan_id} {v.vlan_name}</option>
                            ))}
                          </select>
                        ) : (
                          <select className={`${selCls} flex-1`} value={row.network_id ?? ""} aria-label="Wired network"
                            onChange={(e) => setRow(i, { network_id: e.target.value ? Number(e.target.value) : null })}>
                            <option value="">Network…</option>
                            {networks.map((n) => <option key={n.id} value={n.id}>{n.name} (VLAN {n.vlan_id})</option>)}
                          </select>
                        )}
                      </>
                    )}

                    {/* Only a LEGACY row (a non-canonical key carried over from an older profile)
                        can be removed; the canonical WAN/LAN rows are the fixed wall-plate set. */}
                    {row.legacy && (
                      <button type="button" aria-label="Remove legacy port" onClick={() => removeRow(i)}
                        className="ml-auto text-xs text-ink-muted hover:text-critical">✕</button>
                    )}
                  </div>

                  {/* ACCESS: the "or wired network" affordance -- an access port binds to a VLAN by
                      default; the operator can switch to a named wired network instead. */}
                  {row.role === "access" && (
                    <button type="button"
                      onClick={() => setRow(i, { target: row.target === "vlan" ? "network" : "vlan" })}
                      className="mt-1 text-[11px] font-medium text-brand hover:underline">
                      {row.target === "vlan" ? "or use a wired network" : "or pick a VLAN"}
                    </button>
                  )}
                  {row.legacy && (
                    <p className="mt-1 text-[11px] text-ink-subtle">
                      Carried over from an older profile. New profiles use WAN + LAN1–LAN4.
                    </p>
                  )}

                  {/* UPLINK: the optional trunk allow-list, with live parse feedback. */}
                  {row.role === "uplink" && (
                    <div className="mt-2 flex flex-col gap-1">
                      <label className="flex flex-col gap-1 text-xs">
                        <span className="text-ink-muted">Allowed VLANs (optional)</span>
                        <input value={row.allowed} onChange={(e) => setRow(i, { allowed: e.target.value })}
                          placeholder="100,400,1000-1051" className={`${selCls} font-mono`} aria-label="Allowed VLANs" />
                      </label>
                      <AllowedVlansFeedback text={row.allowed} />
                      {/* Honest scope: Meridian keeps the ports IT manages inside the list; a
                          hand-configured port's VLAN is not removed. Shown only when restricting. */}
                      {row.allowed.trim() && (
                        <span className="text-[11px] text-ink-subtle">
                          Meridian keeps the ports it manages within this list; hand-configured ports aren&apos;t restricted.
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {/* One profile is reusable across models: an AP with fewer LAN ports skips the rest. */}
            <span className="text-[11px] text-ink-subtle">
              LAN ports you leave unnamed and unbound are not mapped. A profile applied to an AP with
              fewer LAN ports applies the ports it has and skips the rest.
            </span>
            {siteVlans.length === 0 && (
              <span className="text-xs text-ink-subtle">Tip: define this site's VLANs (Site &gt; VLANs) so Access ports can bind to them, or use a wired network.</span>
            )}
            {(create.isError || update.isError) && (
              <span className="text-xs text-critical">
                {(create.error as Error)?.message || (update.error as Error)?.message || "Could not save the profile."}
              </span>
            )}
            <div className="md-dialog-footer flex justify-end gap-2">
              <Dialog.Close asChild><Button variant="ghost" type="button">Cancel</Button></Dialog.Close>
              <Button variant="primary" type="submit" disabled={!name.trim() || busy || badAllowed}>{existing ? "Save" : "Create"}</Button>
            </div>
          </form>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

/** Inline parse feedback for an uplink Allowed-VLANs field: helper text when empty, the resolved
 *  count/list when valid, or the malformed tokens when not. Mirrors the backend grammar. */
function AllowedVlansFeedback({ text }: { text: string }) {
  if (!text.trim()) {
    return <span className="text-[11px] text-ink-subtle">Blank = all VLANs; e.g. 100,400,1000-1051</span>;
  }
  const { vlans, invalid } = parseVlanList(text);
  if (invalid.length > 0) {
    return <span className="text-[11px] text-critical">Invalid: {invalid.join(", ")} — use single VLANs or A-B ranges (1-4094)</span>;
  }
  const shown = vlans.slice(0, 12).join(", ");
  return (
    <span className="text-[11px] text-ink-subtle">
      {vlans.length} VLAN{vlans.length === 1 ? "" : "s"}: {shown}{vlans.length > 12 ? ", …" : ""}
    </span>
  );
}
